// WP-C1.7 (ST-4, SE-1): the full-screen PIN-entry gate shown whenever useAppLock().locked is
// true. Verification always happens server-side (POST /api/v1/auth/pin/verify) — this
// component only ever collects the PIN and reacts to the server's own ok/fail/lockout
// decision; the PIN comparison and lockout counter never run client-side.
import { useState } from 'react';
import { ApiError } from '../lib/api';
import { useAppLock, useVerifyPin } from '../lib/appLock';
import { inputClass, primaryButtonInlineClass } from './FormKit';

export function AppLockOverlay(): React.JSX.Element | null {
  const { locked, unlock } = useAppLock();
  const verify = useVerifyPin();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!locked) return null;

  const submit = (): void => {
    setError(null);
    verify.mutate(pin, {
      onSuccess: () => {
        setPin('');
        unlock();
      },
      onError: (err) => {
        setPin('');
        if (err instanceof ApiError && err.code === 'PIN_LOCKED') {
          setError('Too many attempts — try again later.');
        } else {
          setError('Wrong PIN — try again.');
        }
      },
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="App locked"
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg"
    >
      <div className="w-full max-w-xs rounded-[var(--radius-card)] border border-border bg-surface p-6 text-center">
        <p className="mb-4 text-sm font-medium text-ink">Enter your PIN to unlock</p>
        <input
          type="password"
          inputMode="numeric"
          autoFocus
          aria-label="PIN"
          className={inputClass}
          value={pin}
          onChange={(e) => {
            setPin(e.target.value.replace(/\D/g, '').slice(0, 6));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && pin.length >= 4) submit();
          }}
        />
        {error !== null && <p className="mt-2 text-sm text-danger">{error}</p>}
        <button
          type="button"
          className={`mt-4 w-full ${primaryButtonInlineClass}`}
          disabled={pin.length < 4 || verify.isPending}
          onClick={submit}
        >
          Unlock
        </button>
      </div>
    </div>
  );
}
